/**
 * IdCapturePanel — orchestrator that composes DocumentCaptureModule +
 * FieldComparisonDialog and drives the full ID-scan pipeline.
 *
 * Flow (per design doc §13a):
 *   1. Render DocumentCaptureModule (Scanner / Camera / Upload).
 *   2. On capture: store base64 + show preview + reveal "Extract Fields".
 *   3. Click "Extract Fields" → useIdScan.runExtraction()
 *        → IdExtractionService → ExtractedIdFields.
 *   4. On success: immediately patch CIP fields (idType/idNumber/idIssuer/
 *      idIssueDate/idExpiryDate) to person.extra, then render
 *      FieldComparisonDialog for demographics.
 *   5. On address anomaly: render raw content + "manually enter" prompt in
 *      place of the address comparison row.
 *   6. Save the image file to ao_Documents via the v3 Function document upload
 *      route (`POST /v3/documents/upload`) so the scan is retained.
 *
 * Ported from legacy SPFx `shared/cif/IdCapturePanel.tsx` (full component).
 *
 * NOTE: CIP fields live on `person.extra` (Wave-4 promotion pending). The
 * accessor pattern this component mirrors: string-typed reads of
 * `extra.idType / idNumber / idIssuer / idIssueDate / idExpiryDate` plus a
 * `patchExtra`-style merge write (see the PDW CIP sections, e.g.
 * IndividualCipScanSection). The old reference target, the never-mounted
 * `components/compliance/CddCipSection.tsx` orphan, was deleted (BACKLOG-0746).
 *
 * Document upload to ao_Documents is best-effort: when no CIF has been
 * resolved yet (`person.cifId` empty), the save is skipped — the v3 upload
 * endpoint requires `cifId` (HTTP 400 `INVALID_CIF` otherwise). The
 * scan-and-extract flow never blocks on the upload; the officer can still
 * save the cart even if the SharePoint write fails.
 */

import { useEffect, useState, type CSSProperties, type JSX } from 'react';
import { useIdScan } from '../../hooks/useIdScan.js';
import { DocumentCaptureModule } from './DocumentCaptureModule.js';
import { FieldComparisonDialog } from './FieldComparisonDialog.js';
import { TwainBridgeStatus } from './TwainBridgeStatus.js';
import { useApiClient } from '@/contexts/ApiClientContext';
import type { RaoPerson } from '../../types.js';
import type { ExtractedIdFields } from '../../services/IdExtractionService.js';
import {
  estimateBase64Size,
  getPrimaryIdDocCode,
  type AttachedDoc,
} from '../personDialog/shared/attachedDocs.js';

export interface IdCapturePanelProps {
  person: RaoPerson;
  onChange: (patch: Partial<RaoPerson>) => void;
  mode: 'new' | 'existing';
  defaultExpanded?: boolean;
  /**
   * Fires once per successful extraction — lets the parent PersonDialog
   * register the captured image against the DocumentRequirements slot that
   * matches this entity's primary-ID docCode (Individual → GOVT_ID,
   * Minor → GOVT_ID_GUARDIAN, NonCitizen → PASSPORT, etc.). Without this
   * callback the scan panel still operates; the slot grid just won't
   * auto-fulfill.
   */
  onDocumentCaptured?: (doc: AttachedDoc) => void;
  /**
   * Which CIP slot this panel writes to on extraction.
   *   - 'primary'   → idType / idNumber / idIssuer / idIssueDate / idExpiryDate
   *                   (Core Director fields 254/255/256/257/258)
   *   - 'secondary' → idType2 / idNumber2 / idIssuer2 / idIssueDate2 / idExpiryDate2
   *                   (Core Director fields 814/801/813/815/816 — Optional 2nd ID)
   *
   * Defaults to 'primary'. Both modes share the same DocumentCaptureModule +
   * IdExtractionService; only the immediate CIP patch builder branches on
   * which extra-keys to write. Demographics (name/DOB/address/etc.) are
   * suppressed in 'secondary' mode — those came from the primary scan and
   * a 2nd-ID rescan would clobber USPS-validated demographic fields.
   *
   * Per Bill 2026-04-24: the SPFx reference only covered primary; v3
   * extends the same scan→extract flow to the 2nd-ID slot so officers don't
   * have to hand-key Field 814/801/813/815/816 when the cardholder
   * presented a passport or other secondary credential.
   */
  cipSlot?: 'primary' | 'secondary';
  /** Override the panel header label (default: "ID Document Capture"). */
  title?: string;
  /**
   * Optional cart id. When present, the captured image is uploaded under the
   * `{cifId}/cart-{cartId}/cip/` folder in ao_Documents so the artifact is
   * associated with the active cart (and flips Cart.HasRegulatedArtifact on
   * the server). When omitted, the image lands at `{cifId}/cip/`.
   */
  cartId?: string;
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const SHELL: CSSProperties = {
  padding: 12,
  background: 'var(--surface-subtle)',
  borderRadius: 8,
  border: '1px solid var(--surface-border)',
  fontFamily: "'Segoe UI', Roboto, sans-serif",
};

const HEADER: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  cursor: 'pointer',
  marginBottom: 8,
};

const TITLE: CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.8px',
  color: 'var(--pcb-blue)',
  display: 'flex',
  alignItems: 'center',
  gap: 6,
};

const BTN: CSSProperties = {
  padding: '6px 14px',
  borderRadius: 6,
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
  border: 'none',
  fontFamily: "'Segoe UI', Roboto, sans-serif",
};

// ---------------------------------------------------------------------------
// CIP patch builder
// ---------------------------------------------------------------------------

/** Build the immediate CIP-fields patch applied to person.extra as soon as
 *  extraction succeeds (pre-review). The PDW CIP sections read these back via
 *  the string-typed `person.extra` accessor pattern (e.g.
 *  IndividualCipScanSection; the old CddCipSection reference target was a
 *  never-mounted orphan deleted in BACKLOG-0746). The `slot` parameter
 *  switches which CIP family receives the values:
 *    - 'primary'   → idType/idNumber/idIssuer/idIssueDate/idExpiryDate
 *                    (Core Director fields 254/255/256/257/258)
 *    - 'secondary' → idType2/idNumber2/idIssuer2/idIssueDate2/idExpiryDate2
 *                    (Core Director fields 814/801/813/815/816). */
function buildCipPatch(
  person: RaoPerson,
  ex: ExtractedIdFields,
  slot: 'primary' | 'secondary',
): Partial<RaoPerson> {
  const existingExtra = (person.extra ?? {}) as Record<string, unknown>;
  const patch: Record<string, unknown> = { ...existingExtra };
  // Scan emits idTypeCode (v3 shape) but the form-level person.extra key
  // is `idType` to match the rest of the CIP field family (idNumber /
  // idIssuer / idIssueDate / idExpiryDate). Writing under the wrong key
  // was silently dropping the dropdown pre-selection after successful scan
  // even though all the other fields landed.
  const keys = slot === 'secondary'
    ? { type: 'idType2', num: 'idNumber2', issuer: 'idIssuer2', issue: 'idIssueDate2', expiry: 'idExpiryDate2' }
    : { type: 'idType',  num: 'idNumber',  issuer: 'idIssuer',  issue: 'idIssueDate',  expiry: 'idExpiryDate'  };
  if (ex.idTypeCode)    patch[keys.type]   = ex.idTypeCode;
  if (ex.idIssuer)      patch[keys.issuer] = ex.idIssuer;
  if (ex.idNumber)      patch[keys.num]    = ex.idNumber;
  if (ex.idIssueDate)   patch[keys.issue]  = ex.idIssueDate;
  if (ex.idExpiryDate)  patch[keys.expiry] = ex.idExpiryDate;
  return { extra: patch };
}

/** Auto-apply blank demographic fields (status 'blank' rows accept automatically per §13a). */
function buildBlankAutoFillPatch(person: RaoPerson, ex: ExtractedIdFields): Partial<RaoPerson> {
  const out: Partial<RaoPerson> = {};
  if (!person.firstName && ex.firstName)   out.firstName = ex.firstName;
  if (!person.middleName && ex.middleName) out.middleName = ex.middleName;
  if (!person.lastName && ex.lastName)     out.lastName = ex.lastName;
  if (!person.dob && ex.dob)               out.dob = ex.dob;
  if (!person.addr && ex.addr)             out.addr = ex.addr;
  if (!person.city && ex.city)             out.city = ex.city;
  if (!person.state && ex.state)           out.state = ex.state;
  if (!person.zip && ex.zip)               out.zip = ex.zip;
  if (!person.gender && ex.gender)         out.gender = ex.gender;
  return out;
}

// ---------------------------------------------------------------------------
// Best-effort save to ao_Documents
// ---------------------------------------------------------------------------

async function saveImageToLibrary(
  apiClient: ReturnType<typeof useApiClient>,
  person: RaoPerson,
  base64: string,
  mimeType: string,
  rawDocumentType: string | undefined,
  cartId: string | undefined,
): Promise<void> {
  // The v3 upload endpoint REQUIRES cifId (server returns 400 INVALID_CIF
  // otherwise — see DocumentsFunction.Upload line 587). Don't substitute
  // taxId — the SharePoint folder taxonomy is `{cifId}/cart-{cartId}/...`,
  // so a taxId-keyed write would land in the wrong audit bucket. Pre-CIF
  // scans skip the library write and stay in the in-memory dialog state
  // until the CIF resolves (CustAdd / CustInq) and the parent re-fires the
  // upload via the cart commit pipeline.
  const cifId = person.cifId;
  if (!cifId) return;
  const ext = mimeType === 'application/pdf' ? 'pdf'
    : mimeType === 'image/png'              ? 'png'
    : 'jpg';
  const safeType = (rawDocumentType ?? 'ID_SCAN').replace(/[^a-zA-Z0-9]/g, '_');
  const ts = new Date().toISOString().replace(/[:.]/g, '-').substring(0, 19);
  const fileName = `${cifId}_ID_${safeType}_${ts}.${ext}`;

  try {
    await apiClient.retail.documents.upload({
      cifId,
      cartId,
      fileName,
      contentType: mimeType,
      bytesBase64: base64,
      category: 'fsr_upload',
      docCode: 'ID_SCAN',
      notes: `Scanned ID — ${rawDocumentType ?? 'Unknown'}`,
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[IdCapturePanel] save-to-library failed (non-fatal):', err);
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function IdCapturePanel({
  person,
  onChange,
  mode,
  defaultExpanded,
  onDocumentCaptured,
  cipSlot = 'primary',
  title,
  cartId,
}: IdCapturePanelProps): JSX.Element {
  const apiClient = useApiClient();
  // Thread provenance context so the persisted DocumentExtract row links to
  // this cart/person instead of landing orphaned (BACKLOG-0748 follow-on).
  const scan = useIdScan({
    ...(cartId ? { cartId } : {}),
    ...(person.cifId ? { cifId: person.cifId } : {}),
    ...(person.id || person.cifId ? { personRefId: person.id ?? person.cifId } : {}),
  });
  const [expanded, setExpanded] = useState<boolean>(defaultExpanded ?? mode === 'new');
  const isSecondary = cipSlot === 'secondary';
  const headerTitle = title ?? (isSecondary ? '2nd ID Document Capture' : 'ID Document Capture');
  // Click-to-enlarge zoom modal (port of SPFx IdCapturePanel.tsx zoomOpen
  // pattern). Pointer changes to zoom-in on hover; click pops a 90vw/85vh
  // modal with the same image. Closes via scrim click, X button, or Escape.
  const [zoomOpen, setZoomOpen] = useState<boolean>(false);

  // When extraction completes successfully, immediately patch CIP + auto-fill
  // blank demographics, then best-effort upload the image to ao_Documents,
  // AND register the captured image against the entity's primary-ID slot
  // so the Document Requirements grid reflects it as fulfilled.
  const { extracted, imageData, imageMime } = scan;
  useEffect(() => {
    if (!extracted) return;
    const cipPatch = buildCipPatch(person, extracted, cipSlot);
    // Demographics (name/DOB/address/etc.) are blank-auto-filled from the
    // primary scan only. A 2nd-ID scan doesn't re-populate those — the
    // primary already drove the USPS-validated address + name fields, and
    // a passport/secondary credential's demographics would clobber them.
    const blankPatch = isSecondary ? {} : buildBlankAutoFillPatch(person, extracted);
    onChange({ ...cipPatch, ...blankPatch });
    if (imageData && imageMime) {
      void saveImageToLibrary(apiClient, person, imageData, imageMime, extracted.rawDocumentType, cartId);
    }

    // Dialog-level slot fulfillment — requires both (a) an image AND (b)
    // an entity-type → docCode mapping. Trust/Estate/etc. return null from
    // getPrimaryIdDocCode because their entity-level requirements aren't
    // a personal ID (those are filed against the associated Individual).
    // Skip for 'secondary' — the 2nd-ID slot doesn't have an entity-level
    // DocumentRequirements row; the image is retained via saveImageToLibrary
    // above for audit, but it doesn't fulfill the GOVT_ID slot (the primary
    // scan already did that).
    if (!isSecondary && imageData && imageMime && onDocumentCaptured) {
      const docCode = getPrimaryIdDocCode(person.customerType);
      if (docCode) {
        const rawType = extracted.rawDocumentType ?? 'ID';
        const safeLabel = rawType.replace(/[^a-zA-Z0-9 ]/g, '').trim() || 'ID';
        const ext = imageMime === 'application/pdf' ? 'pdf'
          : imageMime === 'image/png' ? 'png'
          : 'jpg';
        onDocumentCaptured({
          docCode,
          source: 'cip-scan',
          filename: `Scanned ${safeLabel}.${ext}`,
          contentType: imageMime,
          sizeBytes: estimateBase64Size(imageData),
          imageDataUrl: `data:${imageMime};base64,${imageData}`,
          fileUrl: '',
          capturedAt: new Date().toISOString(),
        });
      }
    }
    // Keep the dep list narrow — we only want this to fire when a fresh
    // extraction lands. Patch helpers read `person` from closure, which is
    // safe because onChange is expected to be stable-ish and patches are
    // idempotent (same inputs → same patch).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extracted]);

  const badgeText = scan.imagePreviewUrl
    ? (extracted ? 'Extracted' : 'Image loaded')
    : null;

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={HEADER} onClick={() => setExpanded((x) => !x)} role="button" tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setExpanded((x) => !x);
          }
        }}>
        <div style={TITLE}>
          {headerTitle}
          <span
            aria-hidden="true"
            style={{
              fontSize: 12,
              opacity: 0.6,
              display: 'inline-block',
              transition: 'transform 0.2s',
              transform: expanded ? 'rotate(90deg)' : 'none',
            }}
          >
            {'▶'}
          </span>
          <TwainBridgeStatus status={scan.twain.status} />
        </div>
        {!expanded && badgeText && (
          <span style={{ fontSize: 12, color: 'var(--pcb-green)', fontWeight: 500 }}>{badgeText}</span>
        )}
      </div>

      {expanded && (
        <div style={SHELL}>
          <DocumentCaptureModule
            onCapture={scan.setImage}
            twain={scan.twain}
          />

          {scan.imagePreviewUrl && (
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginTop: 12 }}>
              <img
                src={scan.imagePreviewUrl}
                alt="ID preview"
                title="Click to enlarge"
                onClick={() => setZoomOpen(true)}
                style={{
                  maxWidth: 220,
                  maxHeight: 150,
                  borderRadius: 6,
                  border: '1px solid var(--surface-border)',
                  objectFit: 'contain',
                  background: 'var(--surface-card)',
                  cursor: 'zoom-in',
                }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => { void scan.runExtraction(); }}
                    disabled={scan.extracting}
                    style={{
                      ...BTN,
                      background: scan.extracting ? 'var(--ink-500)' : 'var(--ok-fill)',
                      color: 'var(--on-brand)',
                    }}
                  >
                    {scan.extracting ? 'Extracting…' : 'Extract Fields'}
                  </button>
                  <button
                    type="button"
                    onClick={() => scan.reset()}
                    style={{
                      ...BTN,
                      background: 'transparent',
                      color: 'var(--pcb-red)',
                      border: '1px solid var(--surface-border)',
                    }}
                  >
                    Clear
                  </button>
                </div>

                {scan.extractionError && (
                  <div
                    style={{
                      marginTop: 8,
                      padding: '6px 10px',
                      background: 'var(--err-bg)',
                      borderRadius: 6,
                      fontSize: 12,
                      color: 'var(--pcb-red)',
                    }}
                  >
                    Extraction failed: {scan.extractionError}
                  </div>
                )}

                {extracted && extracted.rawDocumentType && (
                  <div style={{ marginTop: 8, fontSize: 12, color: 'var(--ink-600)' }}>
                    Detected: <strong>{extracted.rawDocumentType}</strong>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Address anomaly — show raw content + manual-entry prompt */}
          {extracted?.addressAnomaly && (
            <div
              style={{
                marginTop: 10,
                padding: '10px 12px',
                borderRadius: 8,
                background: 'var(--warn-bg)',
                border: '1px solid var(--warn-line)',
                fontSize: 12,
                color: 'var(--warn-text)',
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 4 }}>
                Address needs manual entry
              </div>
              <div style={{ marginBottom: 4 }}>
                The AI could not confidently parse the address from this ID
                ({extracted.addressAnomaly.reason}). Please enter it manually
                using the printed text as a reference:
              </div>
              {extracted.addressAnomaly.rawContent && (
                <pre
                  style={{
                    margin: '6px 0 0',
                    padding: 6,
                    background: 'var(--surface-subtle)',
                    borderRadius: 4,
                    fontFamily: 'Consolas, monospace',
                    fontSize: 12,
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {extracted.addressAnomaly.rawContent}
                </pre>
              )}
            </div>
          )}

          {/* Demographic comparison — suppressed in 'secondary' mode. The
              primary scan already drove the demographic patches via USPS;
              re-running comparison from a 2nd-ID scan would either restate
              identical values or invite the officer to clobber them with
              passport/secondary-credential data that's typically less
              authoritative for address (passports rarely carry domestic
              address; secondary IDs may be expired). */}
          {!isSecondary && extracted && !extracted.addressAnomaly && (
            <FieldComparisonDialog
              person={person}
              extracted={extracted}
              onAccept={(patch) => onChange(patch)}
            />
          )}

          {/* Comparison without address when address was anomalous */}
          {!isSecondary && extracted?.addressAnomaly && (
            <FieldComparisonDialog
              person={person}
              extracted={{ ...extracted, addr: '', city: '', state: '', zip: '' }}
              onAccept={(patch) => onChange(patch)}
            />
          )}
        </div>
      )}

      {/* ── Zoom modal — full-screen scrim with click-to-close (port of  ── */}
      {/*    SPFx IdCapturePanel.tsx zoom pattern, lines 787-803).         */}
      {zoomOpen && scan.imagePreviewUrl && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Scanned ID enlarged view"
          onClick={() => setZoomOpen(false)}
          onKeyDown={(e) => { if (e.key === 'Escape') setZoomOpen(false); }}
          tabIndex={-1}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.7)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'zoom-out',
          }}
        >
          <div
            style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={scan.imagePreviewUrl}
              alt="Scanned ID — enlarged"
              style={{
                maxWidth: '90vw',
                maxHeight: '85vh',
                borderRadius: 8,
                boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
                objectFit: 'contain',
                background: 'var(--surface-card)',
              }}
            />
            <div style={{ textAlign: 'center', marginTop: 8 }}>
              <button
                type="button"
                onClick={() => setZoomOpen(false)}
                style={{
                  ...BTN,
                  background: 'var(--surface-card)',
                  color: 'var(--ink-700)',
                  border: '1px solid var(--surface-border)',
                  padding: '6px 20px',
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
